import { Component, OnDestroy, OnInit } from '@angular/core';
import { DataService } from 'src/app/data.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import moment from 'moment';

@Component({
  selector: 'app-importasc',
  templateUrl: './importasc.component.html',
  styleUrls: ['./importasc.component.scss'],
})
export class ImportascComponent implements OnInit, OnDestroy {

  sajatIntezmeny: any;
  tanevek: any[] = [];
  selectedEv: number = null;

  file: File = null;
  step: 'upload' | 'preview' | 'done' = 'upload';
  loading = false;

  // előnézet eredménye
  preview: { teachers: any[]; allomany: any[]; summary: any } = null;
  teacherMap: { [ascId: string]: string } = {};   // ascTeacherId -> adid | '__SKIP__'

  // import opciók
  mode: 'replace' | 'append' = 'append';

  /*  Részenként választható import.
   *
   *  Az aSc fájl több, egymástól független dolgot hoz: törzsadatot (tantárgy, osztály,
   *  terem), a tanár-párosítást, a tanmenetet (tantárgyfelosztás: csoportok + heti
   *  óraszám) és a kirakott órarendet. Külön dönthető, melyik menjen, és hogy
   *  kiegészítsük-e csak a hiányzót, vagy a meglévőt is a fájl szerintire igazítsuk.
   */
  reszek: any = {
    tantargy: { be: true, mod: 'kiegeszit' },
    osztaly: { be: true, mod: 'kiegeszit' },
    terem: { be: true, mod: 'kiegeszit' },
    tanar: { be: true, mod: 'kiegeszit' },
    tanmenet: { be: true, mod: 'kiegeszit' },
    orarend: { be: true, mod: 'kiegeszit' },
  };
  reszLista = [
    { kulcs: 'tantargy', cim: 'Tantárgyak', leiras: 'tantárgy-törzs (név, rövidítés)' },
    { kulcs: 'osztaly', cim: 'Osztályok', leiras: 'osztály-törzs (név, évfolyam)' },
    { kulcs: 'terem', cim: 'Termek', leiras: 'tanterem-törzs' },
    { kulcs: 'tanar', cim: 'Tanárok', leiras: 'az aSc-tanár ↔ állomány párosítás rögzítése' },
    { kulcs: 'tanmenet', cim: 'Tanmenet (tantárgyfelosztás)', leiras: 'csoportok, csoport–tantárgy kötés, heti óraszám' },
    { kulcs: 'orarend', cim: 'Órarend', leiras: 'a kirakott órák dátumos sorai + csengetési rend' },
  ];
  intervalType: 'egesz' | 'elsofelev' | 'masodikfelev' | 'sajat' = 'egesz';
  customStart: string = null;
  customEnd: string = null;

  // commit eredménye
  result: any = null;

  /*  KIR-import a párosítatlan tanárokhoz.
   *
   *  Ha az aSc-ben szereplő tanár nincs az állományban, nem kell kilépni az importból:
   *  betölthető a KIR-kivonat (ugyanaz az Excel, amit az Állomány import vár), a
   *  rendszer névre javaslatot tesz, az e-mail cím kézzel javítható (a KIR-ben gyakran
   *  hiányzik vagy régi), és a munkatárs egy gombbal létrejön + párosítódik.
   */
  kirSorok: any[] = [];
  kirFajlNev = '';
  kirValasztas: { [ascId: string]: any } = {};   // ascTeacherId → KIR sor
  kirEmail: { [ascId: string]: string } = {};    // ascTeacherId → szerkesztett e-mail
  kirFut: { [ascId: string]: boolean } = {};

  /*  A loader szövege a szerver fázisjelzéséből.
   *
   *  Az import EGY hosszú kérés (nagy fájlnál percek), a felület addig csak egy pörgő
   *  karikát mutatott – nem derült ki, hol tart, sem hogy halad-e. A kérés mellé küldünk
   *  egy `jobId`-t, a szerver ezzel a kulccsal írja az állapotát, mi pedig másodpercenként
   *  elkérjük és a loader szövegére tesszük.
   */
  private allapotTimer: any = null;

  private ujJobId(): string {
    const c: any = (window as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return 'asc-' + Date.now() + '-' + Math.random().toString(36).slice(2);
  }

  private allapotFigyeles(jobId: string, alap: string) {
    this.allapotLeall();
    this.allapotTimer = setInterval(async () => {
      try {
        const a: any = await firstValueFrom(
          this.http.get(`/api/orarend/ascimport/allapot/${encodeURIComponent(jobId)}`));
        // A `vege` utáni üzenetet nem írjuk ki: a válasz úgyis megérkezik és becsukja a loadert.
        if (!a || a.ismeretlen || a.vege || !a.uzenet) return;
        // Az ion-loading üzenete sima szöveg (a HTML-sablonok nincsenek engedélyezve).
        this.data.updateLoading(`${alap} — ${a.uzenet}`);
      } catch (e) { /* az állapot nem viheti magával az importot */ }
    }, 1000);
  }

  private allapotLeall() {
    if (this.allapotTimer) { clearInterval(this.allapotTimer); this.allapotTimer = null; }
  }

  ngOnDestroy() { this.allapotLeall(); }

  constructor(private http: HttpClient, private data: DataService) { }

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    if (this.intezmenyId) {
      this.http.get<any[]>(`/api/nyilvantartas/tanevek/${this.intezmenyId}`).subscribe(rows => {
        this.tanevek = Array.isArray(rows) ? rows : [];
        // alapértelmezett tanév: a mostani dátum alapján (szept-tól az adott év)
        const now = new Date();
        const defEv = now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1;
        const found = this.tanevek.find(t => Number(t.id) === defEv);
        this.selectedEv = found ? Number(found.id) : (this.tanevek.length ? Number(this.tanevek[this.tanevek.length - 1].id) : defEv);
      });
    }
  }

  get intezmenyId(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.id || '').trim();
  }

  get selectedTanev() {
    return this.tanevek.find(t => Number(t.id) === Number(this.selectedEv));
  }

  // ---- fájlválasztás ----
  onFileSelected(event: any) {
    const f = event?.target?.files?.[0];
    if (f) this.setFile(f);
  }
  onFileDropped(files: any) {
    const f = (files && files[0]) || null;
    if (f) this.setFile(f);
  }
  setFile(f: File) {
    if (f.name.toLowerCase().indexOf('.xml') === -1) {
      this.data.sendToast('Csak aSc Órarend .xml fájlt lehet feltölteni!', 'error');
      return;
    }
    this.file = f;
  }
  clearFile() {
    this.file = null;
    this.preview = null;
    this.result = null;
    this.step = 'upload';
    const el = document.getElementById('ascFileRef') as HTMLInputElement;
    if (el) el.value = null;
  }

  // ---- 1. lépés: előnézet ----
  uploadPreview() {
    if (!this.file || !this.intezmenyId) return;
    this.loading = true;
    const jobId = this.ujJobId();
    this.data.showLoading('aSc fájl feldolgozása, tanárok párosítása...');
    this.allapotFigyeles(jobId, 'aSc fájl feldolgozása');
    const fd = new FormData();
    fd.append('file', this.file, this.file.name);
    fd.append('jobId', jobId);
    this.http.post<any>(`/api/orarend/ascimport/preview/${this.intezmenyId}`, fd).subscribe({
      next: (resp) => {
        this.allapotLeall();
        this.data.hideLoading();
        this.loading = false;
        if (!resp || !resp.ok) {
          this.data.sendToast('Hiba a feldolgozás során: ' + (resp?.error || 'ismeretlen'), 'error');
          return;
        }
        this.preview = resp;
        this.teacherMap = {};
        for (const t of resp.teachers) {
          this.teacherMap[t.ascId] = t.matchedAdid || '';
        }
        this.step = 'preview';
      },
      error: (e) => {
        this.allapotLeall();
        this.data.hideLoading();
        this.loading = false;
        this.data.sendToast('Nem sikerült feldolgozni a fájlt!', 'error');
        console.error(e);
      }
    });
  }

  /** Hány rekord van a fájlban az adott részhez (az előnézet összesítőjéből). */
  reszDb(kulcs: string): string {
    const sum = this.preview?.summary || {};
    switch (kulcs) {
      case 'tantargy': return `${sum.subjects || 0} tantárgy`;
      case 'osztaly': return `${sum.classes || 0} osztály`;
      case 'terem': return `${sum.classrooms || 0} terem`;
      case 'tanar': return `${sum.teachersMatched || 0}/${sum.teachers || 0} párosítva`;
      case 'tanmenet': return `${sum.groups || 0} csoport, ${sum.lessons || 0} óra`;
      case 'orarend': return `${sum.cards || 0} beosztás`;
      default: return '';
    }
  }

  kihagyottOsszes(k: any): number {
    return Object.values(k || {}).reduce((s: number, v: any) => s + (Number(v) || 0), 0) as number;
  }

  /* ── KIR-kivonat betöltése ─────────────────────────────────────────────── */

  onKirFile(event: any) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        this.kirSorok = (XLSX.utils.sheet_to_json(ws, { raw: true, defval: null }) as any[])
          .filter((r) => this.kirNev(r));
        this.kirFajlNev = file.name;
        this.kirJavaslatok();
        this.data.sendToast(`${this.kirSorok.length} személy a KIR-fájlból.`, 'success');
      } catch (err) {
        this.data.sendToast('A KIR-fájlt nem sikerült beolvasni.', 'error');
      }
    };
    reader.readAsBinaryString(file);
  }

  kirNev(r: any): string {
    return `${this.szoveg(r?.['Viselt név vezetéknév'])} ${this.szoveg(r?.['Viselt név keresztnév'])}`.trim();
  }

  private szoveg(v: any): string { return String(v ?? '').trim(); }

  /** Ékezet- és sorrendfüggetlen névkulcs (ugyanaz az elv, mint a szerveroldali párosításnál). */
  private nevKulcs(s: string): string[] {
    return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ')
      .filter((t) => t && t !== 'dr');
  }

  /** Minden párosítatlan aSc-tanárhoz felkínáljuk a névre illő KIR-sort. */
  private kirJavaslatok() {
    for (const t of this.preview?.teachers || []) {
      if (this.teacherMap[t.ascId] && this.teacherMap[t.ascId] !== '__SKIP__') continue;
      const at = this.nevKulcs(t.name);
      if (at.length < 2) continue;
      const talalat = this.kirSorok.filter((r) => {
        const kt = this.nevKulcs(this.kirNev(r));
        return at.every((x) => kt.includes(x));
      });
      if (talalat.length === 1) {
        this.kirValasztas[t.ascId] = talalat[0];
        this.kirEmail[t.ascId] = this.szoveg(talalat[0]['E-mail cím']);
      }
    }
  }

  kirValt(ascId: string, sor: any) {
    this.kirValasztas[ascId] = sor;
    this.kirEmail[ascId] = this.szoveg(sor?.['E-mail cím']);
  }

  /** Az állományi azonosító képzése – ugyanaz a szabály, mint a KIR állomány-importban. */
  private kirAdid(sor: any, email: string): string {
    const kas = this.szoveg(sor['KAS belépés']);
    if (kas) return kas;
    if (email && email.includes('@')) return email.toLowerCase();
    const oktazon = this.szoveg(sor['Oktazon']);
    if (oktazon) return `KIR-${oktazon}`;
    return `ASC-${this.nevKulcs(this.kirNev(sor)).join('.')}`;
  }

  private datum(v: any): string | null {
    if (!v) return null;
    const m = moment(v);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }

  /** A KIR-sorból munkatárs létrehozása + azonnali párosítás az aSc-tanárral. */
  async kirLetrehoz(t: any) {
    const sor = this.kirValasztas[t.ascId];
    if (!sor) { this.data.sendToast('Előbb válassz KIR-sort ehhez a tanárhoz!', 'warning'); return; }
    const email = this.szoveg(this.kirEmail[t.ascId]);
    if (email && !email.includes('@')) { this.data.sendToast('Az e-mail cím nem érvényes.', 'error'); return; }
    const adid = this.kirAdid(sor, email);
    const nev = this.kirNev(sor);
    this.kirFut[t.ascId] = true;
    try {
      const allomany = [{
        adid,
        oktazon: this.szoveg(sor['Oktazon']) || null,
        viselt_vez: this.szoveg(sor['Viselt név vezetéknév']),
        viselt_ker: this.szoveg(sor['Viselt név keresztnév']),
        szul_vez: this.szoveg(sor['Születési név vezetéknév']) || null,
        szul_ker: this.szoveg(sor['Születési név keresztnév']) || null,
        anya_vez: this.szoveg(sor['Anyja neve vezetéknév']) || null,
        anya_ker: this.szoveg(sor['Anyja neve keresztnév']) || null,
        szul_datum: this.datum(sor['Születési dátum']),
        szul_hely: this.szoveg(sor['Születési hely']) || null,
        email: email || null,
        telefonszam: this.szoveg(sor['Telefonszám']) || null,
      }];
      const feladat = [{
        allomany: adid,
        feladatellatas: this.intezmenyId,
        munkakor: this.szoveg(sor['Munkakör']).split(',')[0] || 'pedagógus',
        jogkor: '["pedagogus"]',
        ttf: 1,
        kulso_link: 0,
        email: email || null,
        telefonszam: this.szoveg(sor['Telefonszám']) || null,
        jogviszony_kezd: this.datum(sor['Jogviszony létrejötte']),
        jogviszony_vege: this.datum(sor['Jogviszony megszűnte']),
        rlbtorzsszam: adid,
      }];
      const r: any = await firstValueFrom(this.http.post('/api/import/allomany', [allomany, feladat]));
      if (!r?.ok) throw new Error(r?.error || 'ismeretlen hiba');

      // A frissen létrehozott munkatárs azonnal választható és párosított.
      if (!(this.preview.allomany || []).some((a: any) => a.adid === adid)) {
        this.preview.allomany = [...(this.preview.allomany || []), { adid, nev }]
          .sort((a: any, b: any) => String(a.nev).localeCompare(String(b.nev), 'hu'));
      }
      this.teacherMap[t.ascId] = adid;
      t.matchedName = nev;
      t.letrehozva = true;
      this.data.sendToast(`${nev} létrehozva és párosítva.`, 'success');
    } catch (e: any) {
      this.data.sendToast('A munkatárs létrehozása nem sikerült: ' + (e?.error?.error || e?.message || e), 'error');
    } finally { this.kirFut[t.ascId] = false; }
  }

  get parositatlanTanarok(): any[] {
    return (this.preview?.teachers || [])
      .filter((t: any) => !this.teacherMap[t.ascId] || this.teacherMap[t.ascId] === '__SKIP__');
  }

  get matchedCount(): number {
    if (!this.preview) return 0;
    return this.preview.teachers.filter(t => this.teacherMap[t.ascId] && this.teacherMap[t.ascId] !== '__SKIP__').length;
  }
  get unmatchedCount(): number {
    if (!this.preview) return 0;
    return this.preview.teachers.length - this.matchedCount;
  }

  // ---- 2. lépés: véglegesítés ----
  commit() {
    if (!this.file || !this.intezmenyId || !this.selectedEv) return;
    if (this.intervalType === 'sajat' && (!this.customStart || !this.customEnd)) {
      this.data.sendToast('Egyéni intervallumnál add meg a kezdő és záró dátumot!', 'error');
      return;
    }
    this.loading = true;
    const jobId = this.ujJobId();
    const alapSzoveg = this.reszek.orarend.be
      ? 'Órarend importálása, dátumok kiterjesztése'
      : 'Kiválasztott részek importálása';
    this.data.showLoading(alapSzoveg + '...');
    this.allapotFigyeles(jobId, alapSzoveg);
    const fd = new FormData();
    fd.append('file', this.file, this.file.name);
    fd.append('jobId', jobId);
    fd.append('teacherMap', JSON.stringify(this.teacherMap));
    // A régi `mode` mezőt is küldjük (az órarend-rész módjával), hogy a végpont
    // korábbi hívói se változzanak.
    this.mode = this.reszek.orarend.mod === 'felulir' ? 'replace' : 'append';
    fd.append('mode', this.mode);
    fd.append('reszek', JSON.stringify(this.reszek));
    fd.append('intervalType', this.intervalType);
    fd.append('ev', String(this.selectedEv));
    if (this.intervalType === 'sajat') {
      fd.append('customStart', this.customStart);
      fd.append('customEnd', this.customEnd);
    }
    this.http.post<any>(`/api/orarend/ascimport/commit/${this.intezmenyId}`, fd).subscribe({
      next: (resp) => {
        this.allapotLeall();
        this.data.hideLoading();
        this.loading = false;
        if (!resp || !resp.ok) {
          this.data.sendToast('Hiba az importálás során: ' + (resp?.error || 'ismeretlen'), 'error');
          return;
        }
        this.result = resp;
        this.step = 'done';
        this.data.sendToast(this.reszek.orarend.be
          ? `Sikeres import: ${resp.inserted} órarendi sor`
          : 'Sikeres import: a kiválasztott törzsadat-részek frissültek.', 'success');
      },
      error: (e) => {
        this.allapotLeall();
        this.data.hideLoading();
        this.loading = false;
        this.data.sendToast('Nem sikerült importálni az órarendet!', 'error');
        console.error(e);
      }
    });
  }

  ujImport() {
    this.clearFile();
  }
}
